import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Calendar, TrendingUp, Users, CheckCircle, Clock, DollarSign } from 'lucide-react';
import { api } from '../services/api';
import ApplicationsList from '../components/ApplicationsList';
import LoadingSpinner from '../components/LoadingSpinner';

const Dashboard = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const queryClient = useQueryClient();

  // When date changes, invalidate queries to force refetch
  useEffect(() => {
    queryClient.invalidateQueries(['dashboard-stats']);
    queryClient.invalidateQueries(['dashboard-applications']);
  }, [selectedDate, queryClient]);

  const { data: stats, isLoading: statsLoading } = useQuery(
    ['dashboard-stats', format(selectedDate, 'yyyy-MM-dd')],
    async () => {
      const response = await api.get(`/dashboard/stats?date=${format(selectedDate, 'yyyy-MM-dd')}`);
      console.log('Dashboard stats response:', response.data);
      return response.data;
    }
  );

  const { data: applications, isLoading: applicationsLoading } = useQuery(
    ['dashboard-applications', format(selectedDate, 'yyyy-MM-dd')],
    async () => {
      try {
        // Get all applications for the selected date
        const [allResponse, processingResponse, completedResponse] = await Promise.all([
          api.get(`/dashboard/applications/total?date=${format(selectedDate, 'yyyy-MM-dd')}`),
          api.get(`/dashboard/applications/processing?date=${format(selectedDate, 'yyyy-MM-dd')}`),
          api.get(`/dashboard/applications/completed?date=${format(selectedDate, 'yyyy-MM-dd')}`)
        ]);
        
        return {
          all: allResponse.data?.applications || [],
          processing: processingResponse.data?.applications || [],
          completed: completedResponse.data?.applications || []
        };
      } catch (error) {
        console.error('Error fetching applications:', error);
        return {
          all: [],
          processing: [],
          completed: []
        };
      }
    },
    {
      // Provide default value while loading
      initialData: {
        all: [],
        processing: [],
        completed: []
      }
    }
  );

  const statCards = [
    {
      title: 'Toplam Başvuru',
      value: stats?.stats?.total || 0,
      icon: Users,
      color: 'bg-blue-500',
      textColor: 'text-blue-500'
    },
    {
      title: 'İşlemde',
      value: stats?.stats?.processing || 0,
      icon: Clock,
      color: 'bg-yellow-500',
      textColor: 'text-yellow-500'
    },
    {
      title: 'Tamamlanan',
      value: stats?.stats?.completed || 0,
      icon: CheckCircle,
      color: 'bg-green-500',
      textColor: 'text-green-500'
    },
    {
      title: 'VIP Transfer',
      value: stats?.stats?.vipTransfers || 0,
      icon: TrendingUp,
      color: 'bg-purple-500',
      textColor: 'text-purple-500'
    },
    {
      title: 'Gelir',
              value: `$${stats?.stats?.revenue?.toLocaleString() || 0}`,
      icon: DollarSign,
      color: 'bg-green-600',
      textColor: 'text-green-600'
    }
  ];

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            {format(selectedDate, 'EEEE, d MMMM yyyy', { locale: tr })}
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="date"
              value={format(selectedDate, 'yyyy-MM-dd')}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
        {statCards.map((stat) => (
          <div
            key={stat.title}
            className="bg-white overflow-hidden shadow rounded-lg"
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <stat.icon className={`h-6 w-6 ${stat.textColor}`} />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {stat.title}
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stat.value}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Applications Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Total Applications */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Tüm Başvurular
            </h3>
            {applicationsLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : (
              <ApplicationsList 
                applications={applications?.all || []} 
                maxItems={5}
              />
            )}
          </div>
        </div>

        {/* Processing Applications */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              İşlemde
            </h3>
            {applicationsLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : (
              <ApplicationsList 
                applications={applications?.processing || []} 
                maxItems={5}
              />
            )}
          </div>
        </div>

        {/* Completed Applications */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Tamamlanan
            </h3>
            {applicationsLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : (
              <ApplicationsList 
                applications={applications?.completed || []} 
                maxItems={5}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

